import ResizableEvidence from './ResizableEvidence';
import { curatorRasterTraces } from '../utils/curatorRaster';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Plot from './ThemedPlot';
import AmplitudeProfileWidget from './AmplitudeProfileWidget';
import apiClient from '../api/client';
import { useChannelLabel } from '../context/ChannelNumberingContext';
import { spikeChannelRows } from '../utils/spikeWaveformCards';
import { buildCuratorIsiHistogram } from '../utils/curatorIsi';

export default function CuratorPreviewDiagnostics({ run, selectedIds, tab, settings, recordingConfirmed, names,
  recordingName, canConfirmRecording, onConfirmRecording, onPrimaryChannelsResolved }) {
  const labelChannel = useChannelLabel();
  const [remote, setRemote] = useState({});
  const [reference, setReference] = useState('0');
  const [retry, setRetry] = useState(0);
  const [pageSize, setPageSize] = useState('25');
  const [page, setPage] = useState(0);
  const [isiMaximum, setIsiMaximum] = useState('');
  const [isiBin, setIsiBin] = useState('');
  const allSelected = useMemo(() => {
    const ids = new Set(selectedIds.map(String));
    return run.clusters.filter(unit => ids.has(String(unit.id)));
  }, [run, selectedIds]);
  const countPerPage = pageSize === 'all' || ['amplitude', 'raster'].includes(tab) ? Math.max(1, allSelected.length) : Number(pageSize);
  const pageCount = Math.max(1, Math.ceil(allSelected.length / countPerPage));
  const currentPage = Math.min(page, pageCount - 1);
  const selected = useMemo(() => allSelected.slice(currentPage * countPerPage, (currentPage + 1) * countPerPage), [allSelected, currentPage, countPerPage]);
  useEffect(() => setPage(0), [selectedIds, pageSize, tab]);
  // Metric batches change unit objects without changing waveform requests.
  const extractionSelection = useRef(selected);
  extractionSelection.current = selected;
  const extractionKey = JSON.stringify(selected.map(unit => [unit.id, unit.primaryChannel, unit.spikeTimes]));
  const widthMs = Number(settings.waveformHalfWidthMs);
  const radius = Number(settings.neighborRadius);
  const validWindow = Number.isFinite(widthMs) && widthMs > 0 && widthMs * run.sampleRateHz / 1000 <= 500;
  useEffect(() => {
    const selected = extractionSelection.current;
    const controller = new AbortController();
    setRemote({});
    if (!recordingConfirmed || !['waveforms', 'amplitude'].includes(tab) || !validWindow || !selected.length) return () => controller.abort();
    setRemote({ loading: true });
    const load = async () => {
      const resolved = {};
      const errors = {};
      const entries = [];
      // Resolve and publish one bounded batch at a time, so large selections
      // do not leave the amplitude plot empty until the final unit finishes.
      for (let start = 0; start < selected.length; start += 4) {
      if (controller.signal.aborted) return;
      const batch = selected.slice(start, start + 4);
      const missing = batch.filter(unit => unit.primaryChannel == null && unit.spikeTimes.length);
      if (missing.length) {
        try {
          // run contains sample-normalized times. Predict only inspected units,
          // with a bounded sample count, rather than scanning every unit.
          {
            if (controller.signal.aborted) return;
            const response = await apiClient.predictPrimaryChannels(missing.map(unit => ({
              id: unit.id, primaryChannel: null, spikeTimes: unit.spikeTimes,
            })), 128);
            if (controller.signal.aborted) return;
            if (response.data?.dataset && recordingName && response.data.dataset !== recordingName) {
              throw new Error('The active recording changed. Select the matching recording again.');
            }
            (response.data?.clusters || []).forEach(unit => {
              if (Number.isInteger(unit.primaryChannel) && unit.primaryChannel > 0) resolved[unit.id] = unit.primaryChannel;
            });
          }
        } catch (error) {
          if (controller.signal.aborted) return;
          missing.forEach(unit => { errors[unit.id] = `Unable to resolve the primary channel: ${error.message}`; });
        }
      }
      if (controller.signal.aborted) return;
      entries.push(...await Promise.all(batch.map(async unit => {
      const channel = unit.primaryChannel ?? resolved[unit.id];
      if (!unit.spikeTimes.length) {
        errors[unit.id] = 'No spikes fall inside the selected interval. Increase the interval and run the curator again.';
        return [unit.id, unit.waveforms || []];
      }
      if (channel == null) {
        errors[unit.id] ||= 'No unambiguous primary channel could be found. Check the recording and timestamp units, or provide a primaryChannel in the sorting file.';
        return [unit.id, unit.waveforms || []];
      }
      const count = Math.min(24, unit.spikeTimes.length);
      const spikes = Array.from({ length: count }, (_, i) => {
        const index = Math.floor(i * unit.spikeTimes.length / count);
        return { time: unit.spikeTimes[index], channel: Number(channel), spikeIndex: index, pointIndex: index };
      });
      try {
        const response = await apiClient.getSpikeWaveformCards({ spikes, neighborRadius: radius,
        windowSize: Math.max(1, Math.ceil(widthMs * run.sampleRateHz / 1000)), signal: controller.signal });
        return [unit.id, response.waveforms.map(w => ({ ...w, timeUnit: response.timeUnit || 'samples' }))];
      } catch (error) {
        errors[unit.id] = `Unable to extract waveforms: ${error.message}`;
        return [unit.id, unit.waveforms || []];
      }
      })));
      if (controller.signal.aborted) return;
      setRemote({ loading: start + batch.length < selected.length, completed: start + batch.length,
        waveforms: Object.fromEntries(entries), channels: { ...resolved }, errors: { ...errors } });
      }
      if (!controller.signal.aborted) {
        setRemote({ waveforms: Object.fromEntries(entries), channels: resolved, errors });
        if (Object.keys(resolved).length) onPrimaryChannelsResolved?.(resolved);
      }
    };
    load().catch(error => {
      if (!controller.signal.aborted) setRemote({ error: error.message });
    });
    return () => controller.abort();
  }, [extractionKey, recordingConfirmed, recordingName, radius, run.sampleRateHz, tab, widthMs, validWindow, retry, onPrimaryChannelsResolved]);

  const waveforms = useMemo(() => Object.fromEntries(selected.map(unit => [unit.id,
    remote.waveforms?.[unit.id] || unit.waveforms || []])), [selected, remote.waveforms]);
  const rows = selected.map(original => {
    const unit = { ...original, primaryChannel: original.primaryChannel ?? remote.channels?.[original.id] };
    return { unit, rows: waveforms[unit.id].flatMap(w =>
    spikeChannelRows(w, unit.primaryChannel).map(row => ({ ...row,
      x: row.x.map(value => w.timeUnit === 'milliseconds' || w.timeUnit === 'ms' ? value : value / run.sampleRateHz * 1000),
      raw: row.raw.map(value => settings.invert ? -value : value),
    }))) };
  });
  let amplitudeScale = 1;
  rows.forEach(({ rows: channelRows }) => channelRows.forEach(row => row.raw.forEach(value => { amplitudeScale = Math.max(amplitudeScale, Math.abs(value)); })));
  const clusterData = useMemo(() => ({ clusters: selected.map(unit => ({ ...unit, clusterId: unit.id })) }), [selected]);

  if (!selected.length) return <p className="curator-preview-empty">Select a unit, channel, or metric in the table to inspect its evidence.</p>;
  return <div className="curator-preview-diagnostics">
    <div className="curator-preview-controls" aria-label="Selected unit panels">
      <span role="status">Showing {currentPage * countPerPage + 1}–{Math.min((currentPage + 1) * countPerPage, allSelected.length)} of {allSelected.length} selected units</span>
      {!['amplitude', 'raster'].includes(tab) && <><label>Clusters per page<select value={pageSize} onChange={event => setPageSize(event.target.value)}>
        {[25, 50, 100, 200].map(size => <option key={size} value={size}>{size}</option>)}<option value="all">ALL</option>
      </select></label>
      <button type="button" disabled={currentPage === 0} onClick={() => setPage(currentPage - 1)}>Previous units</button>
      <button type="button" disabled={currentPage + 1 >= pageCount} onClick={() => setPage(currentPage + 1)}>Next units</button></>}
    </div>
    {['waveforms', 'amplitude'].includes(tab) && <>
      {!validWindow && tab === 'amplitude' && <p role="alert">Choose a positive waveform half-width no greater than {(500 / run.sampleRateHz * 1000).toFixed(2)} ms to load amplitude profiles.</p>}
      {!recordingConfirmed && <div className="curator-preview-card">
        <p>To extract waveforms, confirm that these units belong to <strong>{recordingName || 'the selected recording'}</strong>. Missing primary channels will be estimated automatically from their spike times.</p>
        <button type="button" className="curator-preview-primary" disabled={!canConfirmRecording} onClick={onConfirmRecording}>Use this recording and load waveforms</button>
        {!canConfirmRecording && <p>Select a recording in the top banner and match its sample rate in step 1 first.</p>}
      </div>}
      {remote.loading && <p role="status">Resolving missing primary channels and loading waveforms… {remote.completed || 0} of {selected.length} units processed.</p>}
      {remote.error && <p role="alert">{remote.error}</p>}
      {Object.entries(remote.errors || {}).map(([id, message]) => <p role="alert" key={id}>Unit {id}: {message}</p>)}
      {recordingConfirmed && <button type="button" disabled={remote.loading} onClick={() => setRetry(value => value + 1)}>Reload waveforms</button>}
    </>}
    {tab === 'amplitude' && <ResizableEvidence label="Amplitude profiles" defaultWidth={1000} defaultHeight={760}><AmplitudeProfileWidget selectedClusters={selected.map(unit => unit.id)}
      clusterData={clusterData} clusterWaveforms={waveforms} loading={Boolean(remote.loading)} demoMode /></ResizableEvidence>}
    {tab === 'raster' && <>
      <p>Each tick is a spike. Rows are selected units; time is in seconds from the start of the recording. All spikes in the curator interval are included.</p>
      <ResizableEvidence label="Raster plot" defaultWidth={900}>
        {size => <Plot data={curatorRasterTraces(allSelected, run.sampleRateHz, names)} layout={{ autosize: true, height: size.height,
          margin: { l: 95, r: 25, t: 25, b: 50 }, uirevision: `raster:${selectedIds.join(',')}:${run.durationSeconds}`,
          xaxis: { title: { text: 'Time (s)' }, range: [0, run.durationSeconds] },
          yaxis: { title: { text: 'Unit' }, tickvals: allSelected.map((_, i) => i), ticktext: allSelected.map(unit => names[unit.id] || String(unit.id)), autorange: 'reversed' } }}
          style={{ width: '100%', height: '100%' }} useResizeHandler config={{ responsive: true, displaylogo: false }} />}
      </ResizableEvidence>
    </>}
    {tab === 'isi' && <>
      <p>ISIs are the differences between consecutive spike timestamps, converted to milliseconds. The table shows the percentage shorter than 2 ms; this histogram shows the interval counts.</p>
      <div className="curator-preview-controls">
        <label>ISI maximum (ms)<input type="number" min="0.001" step="any" placeholder="Full range" value={isiMaximum} onChange={event => setIsiMaximum(event.target.value)} /></label>
        <label>ISI bin width (ms)<input type="number" min="0.001" step="any" placeholder="Automatic" value={isiBin} onChange={event => setIsiBin(event.target.value)} /></label>
        <button type="button" onClick={() => { setIsiMaximum(''); setIsiBin(''); }}>Full ISI range</button>
        <button type="button" onClick={() => { setIsiMaximum('100'); setIsiBin('0.5'); }}>First 100 ms</button>
      </div>
      {selected.map(unit => {
      const histogram = buildCuratorIsiHistogram(unit.intervals, { maximumMs: isiMaximum, binMs: isiBin });
      return <article key={unit.id}>
      <h3>{names[unit.id] || `Unit ${unit.id}`} · ISI distribution</h3>
      {!unit.intervals.length ? <p>{unit.spikeTimes.length === 1 ? 'This unit has one spike, so there are no intervals to plot. ISI violations are 0% and the ISI check passes.' : 'No spikes fall inside this interval; ISI violations are unavailable.'}</p> : histogram.error ? <p role="alert">{histogram.error}</p> : <>
      <p>{histogram.included.toLocaleString()} of {histogram.total.toLocaleString()} intervals shown · observed range {histogram.minimum.toLocaleString()}–{histogram.maximum.toLocaleString()} ms · bin width {histogram.binWidthMs.toLocaleString()} ms.</p>
      {histogram.excluded > 0 && <p>{histogram.excluded.toLocaleString()} intervals exceed the display limit. Choose Full ISI range to include them.</p>}
      {!histogram.included ? <p role="status">No intervals fall in this display range. Choose Full ISI range. If the observed times look wrong, check the timestamp unit and sample rate in step 1.</p> : <ResizableEvidence label={`Unit ${unit.id} ISI`}>{size => <Plot
        data={[{ type: 'bar', x: histogram.x, y: histogram.counts, width: histogram.widths, marker: { color: '#0f9488' },
          hovertemplate: 'ISI bin center: %{x:.3f} ms<br>Intervals: %{y}<extra></extra>' }]}
        layout={{ autosize: true, height: size.height, margin: { l: 55, r: 20, t: 20, b: 45 },
          uirevision: `${unit.id}:${histogram.maximumMs}:${histogram.binWidthMs}`,
          xaxis: { title: { text: 'Inter-spike interval (ms)' }, range: [0, histogram.maximumMs], autorange: false },
          yaxis: { title: { text: 'Interval count' }, rangemode: 'tozero', tickformat: ',d',
            dtick: Math.max(...histogram.counts) <= 10 ? 1 : undefined }, bargap: 0,
          shapes: [{ type: 'line', x0: 2, x1: 2, y0: 0, y1: 1, yref: 'paper', line: { color: '#d97706', dash: 'dash' } }] }}
        config={{ responsive: true, displaylogo: false }} style={{ width: '100%', height: '100%' }} useResizeHandler />}</ResizableEvidence>}</>}
      <p>The dashed line marks 2 ms. Violation rate uses all intervals, regardless of the display range.</p>
    </article>;
    })}</>}
    {tab === 'waveforms' && <>
      <p>{recordingConfirmed ? 'Raw recording snippets, up to 24 spikes per unit.' : 'Embedded waveforms only. Confirm the matching recording above to load neighboring channels.'} Shared time and amplitude scales; orange lines mark the reference amplitude.</p>
      {!validWindow && <p role="alert">Choose a positive half-width no greater than {(500 / run.sampleRateHz * 1000).toFixed(2)} ms.</p>}
      <label>Reference amplitude <input type="number" step="any" value={reference} onChange={event => setReference(event.target.value)} /></label>
      <div className="curator-preview-waveforms">{rows.map(({ unit, rows: channelRows }) => {
        const channels = [...new Set(channelRows.map(row => String(row.channel)))].sort((a, b) => Number(a) - Number(b));
        const spacing = amplitudeScale * 2.5;
        return <article key={unit.id}><h3>{names[unit.id] || `Unit ${unit.id}`} · CH {labelChannel(unit.primaryChannel) ?? '—'}{remote.channels?.[unit.id] != null && ' (estimated)'}</h3>
          {!channelRows.length ? <p>{remote.loading ? 'Loading waveform samples…' : !recordingConfirmed
            ? 'No embedded waveforms. Use the recording button above to extract them.'
            : remote.errors?.[unit.id] || 'No waveform samples were returned for this unit.'}</p> : <ResizableEvidence label={`Unit ${unit.id} waveform`}>{size => <Plot
            data={channels.flatMap((channel, index) => {
              const sameChannel = channelRows.filter(row => String(row.channel) === channel);
              const x = sameChannel[0].x;
              const aligned = sameChannel.filter(row => row.x.length === x.length && row.x.every((v, i) => v === x[i]));
              const color = channel === String(unit.primaryChannel) ? '#d94848' : '#149b91';
              return [...sameChannel.map(row => ({ type: 'scatter', mode: 'lines', x: row.x,
                y: row.raw.map(value => value + index * spacing), customdata: row.raw,
                hovertemplate: 'Time: %{x:.3f} ms<br>Amplitude: %{customdata:.3f}<extra></extra>',
                opacity: 0.2, line: { color, width: 1 }, showlegend: false })),
              { type: 'scatter', mode: 'lines', x, y: x.map((_, i) => aligned.reduce((sum, row) => sum + row.raw[i], 0) / aligned.length + index * spacing),
                line: { color, width: 3 }, showlegend: false, name: `CH ${labelChannel(channel)} mean` }];
            })}
            layout={{ autosize: true, height: size.height, margin: { l: 60, r: 15, t: 20, b: 45 },
              uirevision: `${unit.id}:${widthMs}:${settings.invert}:${radius}`, xaxis: { title: 'Time (ms)', range: [-widthMs, widthMs] },
              yaxis: { title: 'Channel', tickvals: channels.map((_, i) => i * spacing), ticktext: channels.map(labelChannel),
                range: [-spacing / 2, (channels.length - 0.5) * spacing] },
              shapes: reference !== '' && Number.isFinite(Number(reference)) ? channels.map((_, i) => ({ type: 'line', xref: 'paper', x0: 0, x1: 1,
                y0: i * spacing + Number(reference), y1: i * spacing + Number(reference), line: { color: '#d97706', width: 1, dash: 'dot' } })) : [],
            }} config={{ responsive: true, displaylogo: false }} style={{ width: '100%', height: '100%' }} useResizeHandler />}</ResizableEvidence>}
        </article>;
      })}</div>
    </>}
  </div>;
}
